/* ========================================================================
   © 2026 SpeedToLead Framework. All rights reserved.
   ======================================================================== */
import { useEffect, useState } from "react";
import { api } from "@/lib/api";

let scriptPromise = null;
function loadBuyButtonScript() {
    if (scriptPromise) return scriptPromise;
    if (typeof window === "undefined") return Promise.resolve();
    if (window.customElements && window.customElements.get("stripe-buy-button")) {
        scriptPromise = Promise.resolve();
        return scriptPromise;
    }
    scriptPromise = new Promise((resolve, reject) => {
        const existing = document.querySelector('script[src="https://js.stripe.com/v3/buy-button.js"]');
        if (existing) {
            existing.addEventListener("load", resolve, { once: true });
            existing.addEventListener("error", reject, { once: true });
            return;
        }
        const s = document.createElement("script");
        s.src = "https://js.stripe.com/v3/buy-button.js";
        s.async = true;
        s.onload = resolve;
        s.onerror = reject;
        document.head.appendChild(s);
    });
    return scriptPromise;
}

export default function StripeBuyButton({ buyButtonId, customerEmail, clientReferenceId, onMounted }) {
    const [pubKey, setPubKey] = useState(null);
    const [ready, setReady] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/plans");
                if (cancelled) return;
                setPubKey(data.stripe_publishable_key);
            } catch (e) {
                console.error("Failed to fetch publishable key", e);
            }
            try {
                await loadBuyButtonScript();
                if (!cancelled) {
                    setReady(true);
                    onMounted?.();
                }
            } catch (e) {
                console.error("Failed to load Stripe Buy Button script", e);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [onMounted]);

    if (!ready || !pubKey || !buyButtonId) {
        return (
            <div
                data-testid="stripe-buy-button-loading"
                className="h-12 w-full animate-pulse border border-border bg-zinc-950/60"
                aria-label="Loading Stripe checkout"
            />
        );
    }

    return (
        <div data-testid="stripe-buy-button-host" className="w-full [&_stripe-buy-button]:w-full">
            <stripe-buy-button
                buy-button-id={buyButtonId}
                publishable-key={pubKey}
                customer-email={customerEmail || undefined}
                client-reference-id={clientReferenceId || undefined}
            />
        </div>
    );
}
